import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBag, CreditCard, QrCode, Truck, Copy, Check, MessageCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getTotal, clearCart, generateWhatsAppMessage, removeFromCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [validatingStock, setValidatingStock] = useState(true);
  const [stockErrors, setStockErrors] = useState([]);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    address: '',
    payment_method: 'pix'
  });

  const total = getTotal();

  // Validate stock on page load
  useEffect(() => {
    validateStock();
  }, []);

  const validateStock = async () => {
    setValidatingStock(true);
    setStockErrors([]);
    
    try {
      const response = await axios.get(`${API}/products`);
      const products = response.data;
      const errors = [];

      for (const cartItem of cartItems) {
        const product = products.find(p => p.id === cartItem.id);
        if (!product) {
          errors.push({ id: cartItem.id, name: cartItem.name, issue: 'Produto não encontrado' });
        } else if (product.stock <= 0) {
          errors.push({ id: cartItem.id, name: cartItem.name, issue: 'Sem estoque' });
        } else if (product.stock < cartItem.quantity) {
          errors.push({ id: cartItem.id, name: cartItem.name, issue: `Apenas ${product.stock} disponível(is)`, available: product.stock });
        }
      }

      setStockErrors(errors);
    } catch (error) {
      console.error('Error validating stock:', error);
    } finally {
      setValidatingStock(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      toast.error('Seu carrinho está vazio!');
      return;
    }

    if (stockErrors.length > 0) {
      toast.error('Corrija os problemas de estoque antes de finalizar');
      return;
    }

    if (!formData.address) {
      toast.error('Por favor, informe o endereço de entrega');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        total: total,
        address: formData.address,
        payment_method: formData.payment_method,
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone
      };

      const response = await axios.post(`${API}/orders`, orderData);
      setOrder(response.data);
      toast.success('Pedido criado com sucesso!');
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erro ao criar pedido. Tente novamente.');
      }
      // Revalidate stock after error
      validateStock();
    } finally {
      setLoading(false);
    }
  };

  const copyPixCode = () => {
    if (order?.pix_code) {
      navigator.clipboard.writeText(order.pix_code);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const sendToWhatsApp = () => {
    const whatsappUrl = generateWhatsAppMessage(formData.address, order?.order_number);
    window.open(whatsappUrl, '_blank');
  };

  const finishOrder = () => {
    clearCart();
    navigate('/');
    toast.success('Obrigado pelo seu pedido!');
  };

  if (cartItems.length === 0 && !order) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Carrinho vazio</h1>
          <p className="text-muted-foreground mb-6">Adicione produtos para continuar</p>
          <Button onClick={() => navigate('/produtos')} className="rounded-full">
            Ver Produtos
          </Button>
        </div>
      </div>
    );
  }

  // Order confirmation screen
  if (order) {
    return (
      <div className="min-h-screen bg-background py-8 pb-24" data-testid="order-confirmation">
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-foreground">Pedido Criado!</h1>
            <p className="text-muted-foreground mt-2">
              Número do pedido: <span className="font-bold text-primary">{order.order_number}</span>
            </p>
          </div>

          {/* Order Summary */}
          <div className="bg-card rounded-2xl border border-border p-6 mb-6">
            <h2 className="font-bold text-lg mb-4">Resumo do Pedido</h2>
            <div className="space-y-3">
              {order.items.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.name}</span>
                  <span className="font-medium">R$ {(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-primary">R$ {order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          {order.payment_method === 'pix' ? (
            <div className="bg-card rounded-2xl border border-border p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <QrCode className="w-6 h-6 text-[#25D366]" />
                <h2 className="font-bold text-lg">Pague com PIX</h2>
              </div>
              
              <div className="bg-muted rounded-xl p-4 mb-4 text-center">
                {order.pix_qrcode && (
                  <img 
                    src={`data:image/png;base64,${order.pix_qrcode}`}
                    alt="QR Code PIX"
                    className="w-48 h-48 mx-auto mb-4"
                    data-testid="pix-qrcode"
                  />
                )}
                <p className="text-sm text-muted-foreground mb-2">
                  Escaneie o QR Code ou copie o código abaixo
                </p>
              </div>

              <div className="bg-muted/50 rounded-xl p-3 mb-4">
                <p className="text-xs text-muted-foreground mb-2">Código PIX Copia e Cola:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={order.pix_code || ''}
                    readOnly
                    className="flex-1 bg-white border rounded-lg px-3 py-2 text-xs font-mono truncate"
                    data-testid="pix-code-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyPixCode}
                    className="shrink-0"
                    data-testid="copy-pix-button"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm">
                <p className="font-medium text-yellow-800">Após o pagamento:</p>
                <p className="text-yellow-700 mt-1">
                  Envie o comprovante pelo WhatsApp para confirmar seu pedido.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-2xl border border-border p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-6 h-6 text-primary" />
                <h2 className="font-bold text-lg">Pagamento na Entrega</h2>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm">
                <p className="text-blue-800">
                  Você optou por pagar na entrega com <strong>cartão de débito ou crédito</strong>.
                </p>
                <p className="text-blue-700 mt-2">
                  Aceitamos: Visa, Mastercard, Elo, American Express e outras bandeiras.
                </p>
              </div>
            </div>
          )}

          {/* Delivery Info */}
          <div className="bg-card rounded-2xl border border-border p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-6 h-6 text-primary" />
              <h2 className="font-bold text-lg">Entrega</h2>
            </div>
            <p className="text-sm text-muted-foreground">{order.address}</p>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={sendToWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-bold py-6 rounded-full text-lg"
              data-testid="send-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Enviar Pedido no WhatsApp
            </Button>
            
            <Button
              variant="outline"
              onClick={finishOrder}
              className="w-full rounded-full py-6"
              data-testid="finish-order-button"
            >
              Voltar para a Loja
            </Button>
          </div>

          {/* Order Status */}
          <div className="mt-6 text-center">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              order.payment_status === 'pago' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {order.payment_status === 'pago' ? (
                <><Check className="w-4 h-4" /> Pagamento Confirmado</>
              ) : (
                <>Aguardando Pagamento</>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Checkout form
  return (
    <div className="min-h-screen bg-background pb-24" data-testid="checkout-page">
      {/* Header */}
      <div className="bg-primary text-white py-8">
        <div className="container mx-auto px-4 md:px-8">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <h1 className="text-2xl md:text-3xl font-black">Finalizar Pedido</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Stock Validation Loading */}
        {validatingStock && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
              <span className="text-blue-800">Verificando disponibilidade dos produtos...</span>
            </div>
          </div>
        )}

        {/* Stock Errors */}
        {stockErrors.length > 0 && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-red-800">Problemas com alguns produtos</h3>
              </div>
              <div className="space-y-2">
                {stockErrors.map((error, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-red-100">
                    <div>
                      <p className="font-medium text-red-800">{error.name}</p>
                      <p className="text-sm text-red-600">{error.issue}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => {
                        removeFromCart(error.id);
                        validateStock();
                      }}
                    >
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-sm text-red-600 mt-3">
                Por favor, remova ou ajuste os itens acima para continuar.
              </p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Info */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-bold text-lg mb-4">Seus Dados</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="customer_name">Nome (opcional)</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      placeholder="Seu nome"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone">Telefone (opcional)</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      placeholder="(88) 99999-9999"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Endereço de Entrega *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Rua, número, bairro, referência..."
                      className="mt-1"
                      required
                      data-testid="checkout-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-bold text-lg mb-4">Forma de Pagamento</h2>
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="pix" id="pix" data-testid="payment-pix" />
                    <Label htmlFor="pix" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className="bg-[#25D366]/20 p-2 rounded-lg">
                        <QrCode className="w-5 h-5 text-[#25D366]" />
                      </div>
                      <div>
                        <p className="font-medium">PIX</p>
                        <p className="text-sm text-muted-foreground">Pague agora pelo site</p>
                      </div>
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-4 border rounded-xl hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value="entrega" id="entrega" data-testid="payment-entrega" />
                    <Label htmlFor="entrega" className="flex items-center gap-3 cursor-pointer flex-1">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        <CreditCard className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">Cartão na Entrega</p>
                        <p className="text-sm text-muted-foreground">Débito ou crédito na maquininha</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                type="submit"
                disabled={loading || stockErrors.length > 0 || validatingStock}
                className="w-full bg-primary hover:bg-primary/90 font-bold py-6 rounded-full text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="create-order-button"
              >
                {loading ? 'Criando pedido...' : stockErrors.length > 0 ? 'Corrija os erros acima' : 'Criar Pedido'}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-card rounded-2xl border border-border p-6 sticky top-24">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Resumo
              </h2>
              
              <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                {cartItems.map(item => (
                  <div key={item.id} className="flex gap-3">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">{item.quantity}x R$ {item.price.toFixed(2)}</p>
                    </div>
                    <p className="font-medium text-sm">
                      R$ {(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">R$ {total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
